import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Popper, PopperProps } from 'react-popper';
import { getGlobalStyles } from '@tanium/coreui-mixins';
import { useOverlayMountNode } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';
import { Arrow } from './Arrow';
import { TooltipContentWrapper } from './TooltipContentWrapper';
import { TooltipProps } from './types';

const StyledTooltipContentWrapper =
  styled(TooltipContentWrapper)<{ children: React.ReactNode }>(getGlobalStyles);

/**
 * A styled tooltip wrapper to display UI relative to a reference component.
 */
export const AdvancedTooltip = ({
  children,
  modifiers,
  placement: requestedPlacement,
  positionFixed,
  opacity,
  __UNSAFE_ADDITIONAL_PROPS__,
}: TooltipProps) => {
  const mountNode = useOverlayMountNode();
  const modifiersWithDefaults: PopperProps['modifiers'] = useMemo(
    () => ({
      ...(modifiers || {}),
      preventOverflow: {
        boundariesElement: 'viewport',
        ...(modifiers?.preventOverflow || {}),
      },
    }),
    [modifiers],
  );
  return !children ? null : (
    <Popper
      modifiers={modifiersWithDefaults}
      placement={requestedPlacement}
      positionFixed={positionFixed}
      {...__UNSAFE_ADDITIONAL_PROPS__}
    >
      {({ ref, style, placement, arrowProps }) =>
        ReactDOM.createPortal(
          <StyledTooltipContentWrapper
            innerRef={ref}
            style={style}
            data-placement={placement}
            opacity={opacity}
          >
            <Arrow
              innerRef={arrowProps.ref}
              data-placement={placement}
              style={arrowProps.style}
              opacity={opacity}
            />
            {children}
          </StyledTooltipContentWrapper>,
          mountNode,
        )
      }
    </Popper>
  );
};
